import React from "react";
import { useTranslation } from "react-i18next";

import { Card, Col, Form, Input, InputNumber, Row, Select, Switch, Typography, Upload } from "antd";
import type { FormInstance } from "antd";
import ImgCrop from "antd-img-crop";

import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";

import { Price } from "../../shared/utils/Price";
import { API_URL, getCredentials } from "../../shared/utils/Util";
import DynamicImg from "../../shared/components/ui/DynamicImg";
import { fetchT, getFetchT } from "../../shared/utils/TypedUtil";
import { PointsConfig, effectivePoints } from "../points-config";
import { CreateItemSchemaResponse, UpdateItemSchemaResponse } from "../../types/api";
import {
  ActionSchema,
  ItemModificationSchema,
  ItemSchema,
  ImageManifest,
  MenuType,
} from "../../types/model";
import { useAntdTheme, useNotification } from "../../shared/hooks";

const { Dragger } = Upload;

type ItemSchemaFormProps = {
  restaurantId: string;

  item_schema: ItemSchema;

  menu_type: MenuType;

  // True if this is a new schema being added instead of updating an existing
  // one.
  is_new: boolean;

  // All action schemas, not just ones already assigned to this item schema.
  action_schemas: ActionSchema[];

  // All modification schemas, not just ones already assigned to this item
  // schema.
  item_modification_schemas: ItemModificationSchema[];

  // Form instance owned by the parent modal.
  form: FormInstance;

  // Save loading state owned by the parent modal.
  saveLoading: boolean;
  setSaveLoading: (loading: boolean) => void;

  on_update_success: (new_schema: ItemSchema) => void;
};

export const ItemSchemaForm = (props: ItemSchemaFormProps) => {
  const { createSuccess, updateSuccess, notifyError } = useNotification();
  const { t } = useTranslation();
  const { token } = useAntdTheme();

  const [shippable, setShippable] = React.useState<boolean>(props.item_schema.shippable);

  // When the redeem-rate toggle is on and this item has no points set, the
  // restaurant's rate fills it in — surface that as a hint, not a locked field.
  const [pointsConfig, setPointsConfig] = React.useState<PointsConfig | undefined>(undefined);
  React.useEffect(() => {
    getFetchT<PointsConfig>(
      `${API_URL}/v1/r/${props.restaurantId}/points_config`,
      setPointsConfig,
      () => setPointsConfig(undefined)
    );
  }, [props.restaurantId]);

  const autoRedeemEnabled = pointsConfig?.enabled ?? false;
  const priceString = Form.useWatch("priceString", props.form);
  const pointsValue = Form.useWatch("points", props.form);
  const parsePriceCents = (s: string | undefined): number => {
    if (!s) {
      return 0;
    }
    try {
      const cents = Price.fromString(s).toCents();
      return Number.isFinite(cents) ? cents : 0;
    } catch {
      return 0;
    }
  };
  const fallbackPoints = effectivePoints(
    parsePriceCents(priceString),
    pointsConfig?.rate_per_dollar ?? undefined
  );
  const showRedeemRateHint =
    autoRedeemEnabled &&
    (pointsValue === undefined || pointsValue === null) &&
    fallbackPoints !== undefined;

  const handleSave = (formValues: Omit<ItemSchema, "price"> & { priceString: string }) => {
    props.setSaveLoading(true);

    const schema: ItemSchema = {
      ...formValues,
      // Convert price back into cents.
      price: Price.fromString(formValues.priceString).toCents(),
    };

    if (props.is_new) {
      fetchT<CreateItemSchemaResponse>(
        "POST",
        `${API_URL}/v1/r/${props.restaurantId}/item_schemas`,
        { item_schema: schema },
        (result: CreateItemSchemaResponse) => {
          props.on_update_success({ ...schema, id: result.id });
          createSuccess(schema.name);
          props.setSaveLoading(false);
        },
        (err: unknown) => {
          props.setSaveLoading(false);
          notifyError(t("error"), err);
        }
      );
    } else {
      fetchT<UpdateItemSchemaResponse>(
        "PUT",
        `${API_URL}/v1/r/${props.restaurantId}/item_schemas/${props.item_schema.id}`,
        schema,
        (result: UpdateItemSchemaResponse) => {
          props.on_update_success(schema);
          updateSuccess(schema.name);
          props.setSaveLoading(false);
        },
        (err: unknown) => {
          props.setSaveLoading(false);
          notifyError(t("error"), err);
        }
      );
    }
  };

  // Convert item schema to something more friendly for our form.
  const initial_schema = { ...props.item_schema, priceString: "" };
  {
    // We store price in cents, convert to dollars for display.
    initial_schema.priceString = Price.fromCents(initial_schema.price).toString();

    // Only use id when modifying action and modification schema relationships.
    initial_schema.action_schema_ids = [];
    if (initial_schema.action_schemas) {
      initial_schema.action_schema_ids = initial_schema.action_schemas.map((action) => action.id);
    }

    initial_schema.modification_schema_ids = [];
    if (initial_schema.modification_schemas) {
      initial_schema.modification_schema_ids = initial_schema.modification_schemas.map(
        (mod) => mod.id
      );
    }
  }

  // The form instance lives in the parent modal and persists across
  // open/close cycles. Explicitly sync form values on mount so stale
  // data from a previous open is overwritten.
  React.useEffect(() => {
    props.form.setFieldsValue(initial_schema);
  }, []);

  const action_schema_choices = props.action_schemas.map((action) => (
    <Select.Option key={action.id} value={action.id}>
      <span>{action.name}</span>
    </Select.Option>
  ));
  const item_modification_schema_choices = props.item_modification_schemas.map((mod) => (
    <Select.Option key={mod.id} value={mod.id}>
      <span>{mod.description}</span>
    </Select.Option>
  ));

  // Image upload states.
  const [imgLoading, setImgLoading] = React.useState<boolean>(false);
  const [imgManifest, setImgManifest] = React.useState<ImageManifest | undefined>(
    props.item_schema.image_manifest
  );
  const [imgUploadErr, setImgUploadErr] = React.useState<string | null>(null);

  // Handle legacy item schemas.
  if (
    imgManifest === undefined &&
    props.item_schema.image_url !== undefined &&
    props.item_schema.image_url !== ""
  ) {
    setImgManifest({
      default: {
        url: props.item_schema.image_url,
        type: "image/jpeg",
        width: 0,
        height: 0,
      },
    });
  }

  const uploadButton = (
    <div>
      {imgLoading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>{t("upload_image")}</div>
    </div>
  );
  const uploaderBody =
    imgManifest !== undefined && !imgLoading ? (
      <DynamicImg manifest={imgManifest} alt="item_image" style={{ width: "100%" }} />
    ) : (
      uploadButton
    );

  const handleUploadChange = (info: {
    file: { status?: string; response?: { manifest: ImageManifest; s3_url: string } };
  }) => {
    if (info.file.status === "uploading") {
      setImgLoading(true);
    } else if (info.file.status === "done") {
      setImgLoading(false);
      setImgManifest(info.file.response?.manifest);
      props.form.setFieldsValue({
        image_url: info.file.response?.s3_url,
        image_manifest: info.file.response?.manifest,
      });
    } else if (info.file.status === "error") {
      setImgLoading(false);
      setImgUploadErr(`Failed to upload image: ${info.file.response}`);
    }
  };

  const cardStyle = {
    borderRadius: token.borderRadiusLG,
    border: `1px solid ${token.colorBorder}`,
  };
  const cardBodyStyle = { padding: token.paddingLG };

  return (
    <Form
      form={props.form}
      layout="vertical"
      initialValues={initial_schema}
      colon={false}
      onFinish={handleSave}
    >
      {/* Hidden fields */}
      <Form.Item name="id" hidden />
      <Form.Item name="image_url" hidden />
      <Form.Item name="image_manifest" hidden />
      <Form.Item name="item_schema_group_id" hidden />

      <Row gutter={[token.marginLG, token.marginLG]}>
        {/* Left Column */}
        <Col xs={24} md={16}>
          {/* Basic Information */}
          <Card title={t("menu_item_name")} style={cardStyle} styles={{ body: cardBodyStyle }}>
            <Form.Item
              label={t("menu_item_name")}
              name="name"
              rules={[
                {
                  required: true,
                  message: t("name_is_required"),
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item label={t("receipt_name")} name="alternate_name">
              <Input />
            </Form.Item>
            <Form.Item label={t("kitchen_chinese_name")} name={["intl_name", "zh"]}>
              <Input />
            </Form.Item>
            <Form.Item label={t("kitchen_spanish_name")} name={["intl_name", "es"]}>
              <Input />
            </Form.Item>
            <Form.Item label={t("kitchen_english_name")} name={["intl_name", "en"]}>
              <Input />
            </Form.Item>
            <Form.Item
              label={t("key_example")}
              name="pos_key"
              hidden={props.menu_type !== "dine_in"}
              style={{ marginBottom: 0 }}
            >
              <Input />
            </Form.Item>
          </Card>

          {/* Image */}
          <Card
            title={t("image")}
            style={{ ...cardStyle, marginTop: token.marginLG }}
            styles={{ body: cardBodyStyle }}
          >
            <div style={{ maxWidth: 300 }}>
              <ImgCrop
                aspect={3 / 2}
                grid={true}
                quality={1}
                rotate={true}
                minZoom={0.1}
                fillColor="black"
              >
                <Dragger
                  name="img"
                  headers={{ Authorization: `Basic ${getCredentials()}` }}
                  accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp"
                  showUploadList={false}
                  action={`${API_URL}/v1/r/${props.restaurantId}/user_content/item_images`}
                  beforeUpload={() => {
                    setImgUploadErr(null);
                    setImgLoading(true);
                    return true;
                  }}
                  onChange={handleUploadChange}
                >
                  {uploaderBody}
                </Dragger>
              </ImgCrop>
              {imgUploadErr && <Typography.Text type="danger">{imgUploadErr}</Typography.Text>}
            </div>
          </Card>

          {/* Pricing */}
          <Card
            title={t("price")}
            style={{ ...cardStyle, marginTop: token.marginLG }}
            styles={{ body: cardBodyStyle }}
          >
            <Row gutter={token.marginMD}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={t("price")}
                  name="priceString"
                  rules={[
                    {
                      required: true,
                      message: t("price_is_required"),
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    precision={2}
                    stringMode={true}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={t("points")}
                  name="points"
                  help={
                    showRedeemRateHint
                      ? t("points_redeem_rate_fallback", { points: fallbackPoints })
                      : undefined
                  }
                >
                  <InputNumber min={0} step={1} precision={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              label={t("tax_free")}
              name="tax_free"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Switch checkedChildren={t("yes")} unCheckedChildren={t("no")} />
            </Form.Item>
          </Card>
        </Col>

        {/* Right Column */}
        <Col xs={24} md={8}>
          {/* Settings */}
          <Card title={t("settings")} style={cardStyle} styles={{ body: cardBodyStyle }}>
            <Row gutter={[token.marginMD, 0]}>
              <Col span={12}>
                <Form.Item label={t("sold_out")} name="sold_out" valuePropName="checked">
                  <Switch checkedChildren={t("yes")} unCheckedChildren={t("no")} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t("featured")} name="featured" valuePropName="checked">
                  <Switch checkedChildren={t("yes")} unCheckedChildren={t("no")} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t("print_label")} name="print_label" valuePropName="checked">
                  <Switch checkedChildren={t("yes")} unCheckedChildren={t("no")} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t("shippable")} name="shippable" valuePropName="checked">
                  <Switch
                    checkedChildren={t("yes")}
                    unCheckedChildren={t("no")}
                    onChange={setShippable}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              label={t("weight_grams")}
              name="weight"
              rules={[
                {
                  required: shippable,
                  message: t("weight_is_required_for_shippable"),
                },
              ]}
            >
              <InputNumber min={0} step={1} precision={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              label={t("notification_time_minutes")}
              name="minimum_notification_minutes"
              style={{ marginBottom: 0 }}
            >
              <InputNumber min={1} step={1} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Card>

          {/* Limited Quantity */}
          <Card
            title={t("limited_quantity")}
            style={{ ...cardStyle, marginTop: token.marginLG }}
            styles={{ body: cardBodyStyle }}
          >
            <Form.Item
              label={t("quantity_limit")}
              name="quantity_limit"
              help={t("quantity_limit_help")}
              style={{ marginBottom: token.marginMD }}
            >
              <InputNumber
                min={1}
                step={1}
                precision={0}
                style={{ width: "100%" }}
                placeholder={t("quantity_limit_placeholder")}
              />
            </Form.Item>
            <Form.Item
              label={t("per_order_quantity_limit")}
              name="per_order_quantity_limit"
              help={t("per_order_quantity_limit_help")}
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={1}
                step={1}
                precision={0}
                style={{ width: "100%" }}
                placeholder={t("per_order_quantity_limit_placeholder")}
              />
            </Form.Item>
          </Card>

          {/* Kitchen & Customization */}
          <Card
            title={t("actions_workstation")}
            style={{ ...cardStyle, marginTop: token.marginLG }}
            styles={{ body: cardBodyStyle }}
          >
            <Form.Item label={t("actions_workstation")} name="action_schema_ids">
              <Select mode="multiple" allowClear={true} showSearch={false}>
                {action_schema_choices}
              </Select>
            </Form.Item>
            <Form.Item label={t("modifications_customization")} name="modification_schema_ids">
              <Select mode="multiple" allowClear={true} showSearch={false}>
                {item_modification_schema_choices}
              </Select>
            </Form.Item>
            <Form.Item label={t("notes")} name="notes" style={{ marginBottom: 0 }}>
              <Input />
            </Form.Item>
          </Card>
        </Col>
      </Row>
    </Form>
  );
};
